import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api, extractAxiosError } from "../lib/api";
import {
    ADMIN_DASHBOARD,
    getLoginPortalRedirect,
    getRedirectForRealm,
    isSuperadmin,
    LOGIN_REALM,
    SESSION_TYPE,
    validateLoginRealm,
} from "../lib/authRouting";
import {
    STORAGE_KEYS,
    clearAuthStorage,
    getStoredToken,
    isValidOrgShape,
    isValidUserShape,
    persistActiveOrgId,
    persistToken,
    resolveStoredActiveOrgId,
    safeRemoveItem,
    clearOrgScopedVolatileState,
    markLeftWorkspace,
    clearLeftWorkspaceFlag,
} from "../lib/clientStorage";

const AuthStateContext = createContext(null);
const AuthActionsContext = createContext(null);

export const AUTH_PHASE = {
    CHECKING: "checking",
    ANONYMOUS: "anonymous",
    REDIRECTING: "redirecting",
    AUTHENTICATED: "authenticated",
};

function enrichOrgFromLogin(org) {
    if (!org) return null;
    return { ...org, role: org.role || "member" };
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [memberships, setMemberships] = useState([]);
    const [activeOrg, setActiveOrg] = useState(null);
    const [authPhase, setAuthPhase] = useState(AUTH_PHASE.CHECKING);
    const [sessionType, setSessionType] = useState(null);
    const [pendingRedirect, setPendingRedirect] = useState(null);
    const [orgReady, setOrgReady] = useState(false);
    const [sessionReady, setSessionReady] = useState(false);
    const pendingSessionRef = useRef(null);
    const authSyncGeneration = useRef(0);

    const isStaleAuthSync = useCallback((generation) => (
        generation != null && generation !== authSyncGeneration.current
    ), []);

    const applyMembershipState = useCallback(({ memberships: nextMemberships = [], activeOrg: nextActiveOrg = null } = {}) => {
        const membershipsList = Array.isArray(nextMemberships) ? nextMemberships.filter(isValidOrgShape) : [];
        let active = isValidOrgShape(nextActiveOrg) ? nextActiveOrg : null;
        if (active && !membershipsList.some((m) => m.id === active.id)) {
            active = null;
        }
        if (!active && membershipsList.length) {
            active = membershipsList[0];
        }
        if (!membershipsList.length) {
            active = null;
        }

        setMemberships(membershipsList);
        setActiveOrg(active);
        persistActiveOrgId(active?.id || null);
        if (membershipsList.length) clearLeftWorkspaceFlag();
        setOrgReady(true);
        return { memberships: membershipsList, activeOrg: active };
    }, []);

    const clearLocalSession = useCallback(() => {
        authSyncGeneration.current += 1;
        clearAuthStorage();
        pendingSessionRef.current = null;
        setPendingRedirect(null);
        setSessionType(null);
        setUser(false);
        setMemberships([]);
        setActiveOrg(null);
        setOrgReady(true);
        setSessionReady(true);
        setAuthPhase(AUTH_PHASE.ANONYMOUS);
    }, []);

    const applyMe = useCallback((data, { generation } = {}) => {
        if (isStaleAuthSync(generation)) return false;
        if (!data || !isValidUserShape(data.user)) {
            console.error("[auth] Invalid auth profile shape from /auth/me");
            return false;
        }
        const membershipsList = Array.isArray(data.memberships) ? data.memberships.filter(isValidOrgShape) : [];
        const storedOrgId = resolveStoredActiveOrgId(membershipsList);
        const fromStored = storedOrgId ? membershipsList.find((m) => m.id === storedOrgId) : null;
        const serverOrg = isValidOrgShape(data.active_org) ? data.active_org : null;
        const fromServer = serverOrg && membershipsList.some((m) => m.id === serverOrg.id) ? serverOrg : null;
        let active = fromStored || fromServer || membershipsList[0] || null;
        if (active && !membershipsList.some((m) => m.id === active.id)) {
            active = membershipsList[0] || null;
        }
        if (!membershipsList.length) {
            active = null;
        }

        setUser(data.user);
        setSessionType(data.session_type || SESSION_TYPE.APP);
        setMemberships(membershipsList);
        setActiveOrg(active);
        if (active?.id) persistActiveOrgId(active.id);
        else safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
        setOrgReady(true);
        return true;
    }, [isStaleAuthSync]);

    const applySessionFromAuthResponse = useCallback((data, realm = LOGIN_REALM.APP) => {
        if (!data?.user || !isValidUserShape(data.user)) return;
        setUser(data.user);
        setSessionType(data.session_type || (realm === LOGIN_REALM.ADMIN ? SESSION_TYPE.ADMIN : SESSION_TYPE.APP));
        if (realm === LOGIN_REALM.ADMIN || isSuperadmin(data.user)) {
            setMemberships([]);
            setActiveOrg(null);
            safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
        } else if (Array.isArray(data.memberships)) {
            const membershipsList = data.memberships.filter(isValidOrgShape);
            const preferred = isValidOrgShape(data.active_org)
                ? data.active_org
                : isValidOrgShape(data.org)
                    ? enrichOrgFromLogin(data.org)
                    : null;
            const active = preferred || membershipsList[0] || null;
            setMemberships(membershipsList);
            setActiveOrg(active);
            if (active?.id) persistActiveOrgId(active.id);
            else safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
        } else {
            const org = enrichOrgFromLogin(data.org);
            if (org && isValidOrgShape(org)) {
                setMemberships([org]);
                setActiveOrg(org);
                persistActiveOrgId(org.id);
            } else {
                setMemberships([]);
                setActiveOrg(null);
                safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
            }
        }
        setOrgReady(true);
    }, []);

    const refreshMe = useCallback(async ({ clearOnFailure = true, background = false, generation } = {}) => {
        const syncGeneration = generation ?? authSyncGeneration.current;
        const token = getStoredToken();
        if (!token) {
            if (clearOnFailure && !background) {
                clearLocalSession();
            }
            return { ok: false, error: new Error("No token") };
        }

        try {
            const { data } = await api.get("/auth/me");
            if (isStaleAuthSync(syncGeneration)) {
                return { ok: false, stale: true };
            }
            if (!applyMe(data, { generation: syncGeneration })) {
                if (clearOnFailure && !background) {
                    clearLocalSession();
                }
                return { ok: false, error: new Error("Invalid auth profile shape") };
            }
            if (isSuperadmin(data?.user) && data?.session_type !== SESSION_TYPE.ADMIN) {
                clearLocalSession();
                return { ok: false, error: new Error("Superadmin must use admin portal") };
            }
            setAuthPhase(AUTH_PHASE.AUTHENTICATED);
            return { ok: true, data };
        } catch (e) {
            console.error("[auth] GET /auth/me failed:", e.response?.status, e.response?.data ?? e.message);
            if (clearOnFailure && !background) {
                clearLocalSession();
            }
            return { ok: false, error: e };
        }
    }, [applyMe, clearLocalSession, isStaleAuthSync]);

    useEffect(() => {
        const token = getStoredToken();
        if (!token) {
            clearLocalSession();
            return undefined;
        }

        let cancelled = false;
        const bootstrapTimeout = window.setTimeout(() => {
            if (cancelled) return;
            console.warn("[auth] session bootstrap timed out — clearing stale session");
            clearLocalSession();
        }, 8000);

        (async () => {
            try {
                const result = await refreshMe();
                if (cancelled) return;
                if (result.ok) {
                    if (isSuperadmin(result.data?.user) && result.data?.session_type !== SESSION_TYPE.ADMIN) {
                        clearLocalSession();
                        return;
                    }
                    setAuthPhase(AUTH_PHASE.AUTHENTICATED);
                } else {
                    setAuthPhase(AUTH_PHASE.ANONYMOUS);
                }
            } catch (err) {
                if (cancelled) return;
                console.error("[auth] bootstrap failed:", err);
                clearLocalSession();
            } finally {
                if (!cancelled) {
                    setSessionReady(true);
                    setAuthPhase((phase) => (
                        phase === AUTH_PHASE.CHECKING ? AUTH_PHASE.ANONYMOUS : phase
                    ));
                }
                window.clearTimeout(bootstrapTimeout);
            }
        })();

        return () => {
            cancelled = true;
            window.clearTimeout(bootstrapTimeout);
        };
    }, [refreshMe, clearLocalSession]);

    const completeAuthSession = useCallback((data, realm = LOGIN_REALM.APP) => {
        const check = validateLoginRealm(data.user, realm);
        if (!check.allowed) {
            safeRemoveItem(STORAGE_KEYS.TOKEN);
            pendingSessionRef.current = null;
            return {
                ok: false,
                rejected: true,
                reason: check.reason,
                error: check.message,
                redirectTo: check.redirectTo,
            };
        }

        if (data.token) {
            persistToken(data.token);
        }
        authSyncGeneration.current += 1;
        applySessionFromAuthResponse(data, realm);
        pendingSessionRef.current = null;
        setPendingRedirect(null);
        setAuthPhase(AUTH_PHASE.AUTHENTICATED);
        setSessionReady(true);
        void refreshMe({ clearOnFailure: false, background: true, generation: authSyncGeneration.current });
        return { ok: true, redirectTo: getRedirectForRealm(realm) };
    }, [applySessionFromAuthResponse, refreshMe]);

    const handleAuthResponse = useCallback((data, realm = LOGIN_REALM.APP) => {
        return completeAuthSession(data, realm);
    }, [completeAuthSession]);

    const login = useCallback(async (email, password, realm = LOGIN_REALM.APP) => {
        const endpoint = realm === LOGIN_REALM.ADMIN ? "/admin/login" : "/auth/login";
        try {
            const { data } = await api.post(endpoint, { email, password });
            if (data.two_factor_required) {
                return { ok: false, two_factor_required: true, challenge_token: data.challenge_token, loginRealm: realm };
            }
            return handleAuthResponse(data, realm);
        } catch (e) {
            const msg = extractAxiosError(e, "Login failed");
            const portal = getLoginPortalRedirect(e.response?.status, msg, realm);
            console.error(`[auth] POST ${endpoint} failed:`, e.response?.status, e.response?.data ?? e.message);
            if (portal) {
                return {
                    ok: false,
                    rejected: true,
                    error: msg,
                    redirectTo: portal.redirectTo,
                    reason: portal.reason,
                };
            }
            return { ok: false, error: msg };
        }
    }, [handleAuthResponse]);

    const verify2fa = useCallback(async (challenge_token, code, realm = LOGIN_REALM.APP) => {
        const endpoint = realm === LOGIN_REALM.ADMIN ? "/admin/2fa/verify" : "/auth/2fa/verify";
        try {
            const { data } = await api.post(endpoint, { challenge_token, code });
            return handleAuthResponse(data, realm);
        } catch (e) {
            const msg = extractAxiosError(e, "Verification failed");
            const portal = getLoginPortalRedirect(e.response?.status, msg, realm);
            console.error(`[auth] POST ${endpoint} failed:`, e.response?.status, e.response?.data ?? e.message);
            if (portal) {
                return {
                    ok: false,
                    rejected: true,
                    error: msg,
                    redirectTo: portal.redirectTo,
                    reason: portal.reason,
                };
            }
            return { ok: false, error: msg };
        }
    }, [handleAuthResponse]);

    const register = useCallback(async (name, email, password, org_name, accepted_terms = false) => {
        try {
            const { data } = await api.post("/auth/register", {
                name,
                email,
                password,
                org_name,
                accepted_terms,
            });
            if (isSuperadmin(data.user)) {
                clearLocalSession();
                return {
                    ok: false,
                    rejected: true,
                    error: "Superadmin accounts cannot be created via registration.",
                    redirectTo: ADMIN_DASHBOARD,
                };
            }
            return handleAuthResponse(data, LOGIN_REALM.APP);
        } catch (e) {
            const msg = extractAxiosError(e, "Registration failed");
            console.error("[auth] POST /auth/register failed:", e.response?.status, e.response?.data ?? e.message);
            return { ok: false, error: msg };
        }
    }, [handleAuthResponse, clearLocalSession]);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
        clearLocalSession();
    }, [clearLocalSession]);

    const applyInviteSession = useCallback((data) => {
        if (data?.org_id) persistActiveOrgId(data.org_id);
        if (Array.isArray(data?.memberships)) {
            authSyncGeneration.current += 1;
            const preferredId = data.org_id || data.active_org?.id;
            const active = data.active_org
                || (preferredId ? data.memberships.find((m) => m.id === preferredId) : null)
                || data.memberships[0]
                || null;
            if (data.user) {
                applyMe({
                    user: data.user,
                    memberships: data.memberships,
                    active_org: active,
                    session_type: data.session_type || SESSION_TYPE.APP,
                });
            } else {
                applyMembershipState({ memberships: data.memberships, activeOrg: active });
            }
            setAuthPhase(AUTH_PHASE.AUTHENTICATED);
            setSessionReady(true);
            return Boolean(data.user || active);
        }
        return false;
    }, [applyMe, applyMembershipState]);

    const leaveWorkspace = useCallback(async (orgId) => {
        if (!orgId) return { ok: false, error: new Error("No workspace selected") };
        try {
            const { data } = await api.post(`/orgs/${orgId}/leave`);
            authSyncGeneration.current += 1;
            clearOrgScopedVolatileState();
            safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
            safeRemoveItem(STORAGE_KEYS.ACTIVE_POOL);

            const enriched = Array.isArray(data?.memberships) ? data.memberships.filter(isValidOrgShape) : [];
            const active = isValidOrgShape(data?.active_org) && enriched.some((m) => m.id === data.active_org.id)
                ? data.active_org
                : enriched[0] || null;

            applyMembershipState({ memberships: enriched, activeOrg: active });
            if (!enriched.length) markLeftWorkspace();
            setAuthPhase(AUTH_PHASE.AUTHENTICATED);
            setSessionReady(true);
            return { ok: true, data: { ...data, memberships: enriched, active_org: active } };
        } catch (e) {
            return { ok: false, error: e };
        }
    }, [applyMembershipState]);

    const switchOrg = useCallback((orgId) => {
        if (user && isSuperadmin(user)) return;
        const next = memberships.find((m) => m.id === orgId);
        if (next) {
            setActiveOrg(next);
            persistActiveOrgId(orgId);
        }
    }, [memberships, user]);

    const setUserAvatar = useCallback((avatarUrl, userPatch) => {
        setUser((prev) => (prev && typeof prev === "object" ? { ...prev, avatar_url: avatarUrl, ...userPatch } : prev));
    }, []);

    const setTokenFromInvite = useCallback(async (session) => {
        const token = typeof session === "string" ? session : session?.token;
        if (!token) {
            clearLocalSession();
            return { ok: false };
        }
        persistToken(token);
        if (session?.org_id) persistActiveOrgId(session.org_id);
        authSyncGeneration.current += 1;

        if (session?.user && Array.isArray(session?.memberships)) {
            const preferredId = session.org_id || session.active_org?.id;
            const active = session.active_org
                || (preferredId ? session.memberships.find((m) => m.id === preferredId) : null)
                || session.memberships[0]
                || null;
            applyMe({
                user: session.user,
                memberships: session.memberships,
                active_org: active,
                session_type: session.session_type || SESSION_TYPE.APP,
            });
            setAuthPhase(AUTH_PHASE.AUTHENTICATED);
            setSessionReady(true);
            return { ok: true };
        }

        setAuthPhase(AUTH_PHASE.CHECKING);
        const me = await refreshMe({ clearOnFailure: false, background: false, generation: authSyncGeneration.current });
        if (!me.ok) {
            clearLocalSession();
        } else if (isSuperadmin(me.data?.user)) {
            setMemberships([]);
            setActiveOrg(null);
            safeRemoveItem(STORAGE_KEYS.ACTIVE_ORG);
            setAuthPhase(AUTH_PHASE.AUTHENTICATED);
        } else {
            setAuthPhase(AUTH_PHASE.AUTHENTICATED);
        }
        setSessionReady(true);
        return me;
    }, [applyMe, refreshMe, clearLocalSession]);

    const actionsValue = useMemo(() => ({
        login,
        register,
        logout,
        refreshMe,
        leaveWorkspace,
        switchOrg,
        setTokenFromInvite,
        applyInviteSession,
        verify2fa,
        setUserAvatar,
        finalizeAuthTransition: applyInviteSession,
        completeAuthSession,
    }), [
        login,
        register,
        logout,
        refreshMe,
        leaveWorkspace,
        switchOrg,
        setTokenFromInvite,
        applyInviteSession,
        verify2fa,
        setUserAvatar,
        completeAuthSession,
    ]);

    const stateValue = useMemo(() => ({
        user,
        memberships,
        activeOrg,
        authPhase,
        sessionType,
        pendingRedirect,
        orgReady,
        sessionReady,
    }), [
        user,
        memberships,
        activeOrg,
        authPhase,
        sessionType,
        pendingRedirect,
        orgReady,
        sessionReady,
    ]);

    return (
        <AuthStateContext.Provider value={stateValue}>
            <AuthActionsContext.Provider value={actionsValue}>
                {children}
            </AuthActionsContext.Provider>
        </AuthStateContext.Provider>
    );
}

export function useAuthState() {
    const ctx = useContext(AuthStateContext);
    if (!ctx) throw new Error("useAuthState must be used within AuthProvider");
    return ctx;
}

export function useAuthActions() {
    const ctx = useContext(AuthActionsContext);
    if (!ctx) throw new Error("useAuthActions must be used within AuthProvider");
    return ctx;
}

export function useAuth() {
    return { ...useAuthState(), ...useAuthActions() };
}

export function useActiveRole() {
    const { activeOrg } = useAuth();
    return activeOrg?.role || "member";
}

export function canApprove(role) {
    return role === "owner" || role === "admin";
}
